import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { XStack, YStack } from "tamagui";
import { z } from "zod";

import {
  type MedicareCardReaderOutput,
  VerificationStatus,
} from "@/api/generated/models";
import { useUpdateUserProfile } from "@/api/mutations/userProfile";
import { useGetUserProfile } from "@/api/queries/userProfile";
import {
  Button,
  Checkbox,
  Grid,
  Input,
  Paragraph,
  getGridLayout,
} from "@/components";
import SkipOrSubmit from "@/conversation/AskUserQuestionsPanel/SkipOrSubmit";
import { mbiZodString } from "@/forms/mbiSchema";

// Name and MBI are both printed on the Medicare card, so this form doubles as the confirmation
// screen for a card photo. Date of birth and sex at birth are not on the card and are collected
// with the rest of the profile during enrollment.
const medicareInsuranceSchema = z.object({
  firstName: z.string().trim().min(1, "First name is required"),
  lastName: z.string().trim().min(1, "Last name is required"),
  mbi: mbiZodString,
  userIsPolicyHolder: z.boolean(),
});

type MedicareInsuranceForm = z.infer<typeof medicareInsuranceSchema>;

export default function MedicareInsuranceManualStep({
  medicareCard,
  onTakePhoto,
  onSubmitted,
}: {
  medicareCard: MedicareCardReaderOutput | null;
  onTakePhoto: () => void;
  onSubmitted: () => void;
}) {
  const { mutate: updateUserProfile, isPending } = useUpdateUserProfile();
  const { data: profile, isLoading: profileLoading } = useGetUserProfile();
  const isIdVerified =
    profile?.idVerification?.status === VerificationStatus.approved;
  const isInsuranceLocked = Boolean(
    profile?.lockedFields?.includes("insurance.medicare_beneficiary_id"),
  );
  const cardName = isIdVerified ? null : medicareCard;
  const cardNumber = isInsuranceLocked ? null : medicareCard;
  const { control, handleSubmit, reset } = useForm<MedicareInsuranceForm>({
    resolver: zodResolver(medicareInsuranceSchema),
    mode: "onBlur",
    defaultValues: {
      firstName: "",
      lastName: "",
      mbi: "",
      userIsPolicyHolder: true,
    },
  });

  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    if (seeded || profileLoading) {
      return;
    }
    reset({
      firstName: cardName?.first_name ?? profile?.firstName ?? "",
      lastName: cardName?.last_name ?? profile?.lastName ?? "",
      mbi:
        cardNumber?.medicare_beneficiary_id ??
        profile?.medicareBeneficiaryId ??
        "",
      userIsPolicyHolder: profile?.userIsPolicyHolder ?? true,
    });
    setSeeded(true);
  }, [seeded, profileLoading, profile, cardName, cardNumber, reset]);

  const onSubmit = handleSubmit(({ mbi, ...rest }) => {
    if (isInsuranceLocked) {
      const { firstName, lastName } = rest;
      updateUserProfile(
        { data: { firstName, lastName } },
        { onSuccess: onSubmitted },
      );
      return;
    }
    updateUserProfile(
      { data: { ...rest, medicareBeneficiaryId: mbi } },
      { onSuccess: onSubmitted },
    );
  });

  return (
    <>
      <Grid paddingHorizontal="$150">
        <Controller
          control={control}
          name="firstName"
          render={({ field: { onChange, onBlur, value }, fieldState }) => (
            <Input.Controlled
              label="First name"
              readonly={isIdVerified}
              value={value}
              onChangeText={onChange}
              onBlur={onBlur}
              errorText={fieldState.error?.message}
              rootProps={getGridLayout(6)}
            />
          )}
        />
        <Controller
          control={control}
          name="lastName"
          render={({ field: { onChange, onBlur, value }, fieldState }) => (
            <Input.Controlled
              label="Last name"
              readonly={isIdVerified}
              value={value}
              onChangeText={onChange}
              onBlur={onBlur}
              errorText={fieldState.error?.message}
              rootProps={getGridLayout(6)}
            />
          )}
        />
        <Controller
          control={control}
          name="mbi"
          render={({ field: { onChange, onBlur, value }, fieldState }) => (
            <Input.Controlled
              label="Medicare number (MBI)"
              readonly={isInsuranceLocked}
              value={value}
              onChangeText={onChange}
              onBlur={onBlur}
              autoCapitalize="characters"
              autoCorrect={false}
              errorText={fieldState.error?.message}
              rootProps={getGridLayout(6)}
            />
          )}
        />
        {isInsuranceLocked && (
          <Paragraph color="$textSecondary" size="$s" {...getGridLayout(12)}>
            Your Medicare number is locked for your ACCESS enrollment. Contact
            support to update it.
          </Paragraph>
        )}
      </Grid>
      <YStack
        gap="$150"
        paddingHorizontal="$150"
        marginTop="$250"
        paddingBottom="$150"
      >
        <Controller
          control={control}
          name="userIsPolicyHolder"
          render={({ field: { onChange, value } }) => (
            <Checkbox
              labelProps={{ fontSize: "$sm", fontWeight: "normal" }}
              rootProps={{ width: "100%" }}
              label="I am the insurance policy holder"
              checked={value}
              disabled={isInsuranceLocked}
              onCheckedChange={(checked) => onChange(checked === true)}
            />
          )}
        />
        <XStack justifyContent="flex-end" alignItems="center" gap="$200">
          {/* Arriving from a scanned card, the photo has already been taken. */}
          {!medicareCard && !isInsuranceLocked && (
            <Button mode="secondary" onPress={onTakePhoto}>
              Take photo of card
            </Button>
          )}
          <SkipOrSubmit
            showSkip={false}
            showSubmit
            submitDisabled={isPending || profileLoading}
            isLast={false}
            onSkip={onSubmit}
            onSubmit={onSubmit}
          />
        </XStack>
      </YStack>
    </>
  );
}
